import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Heart,
    MessageCircle,
    Eye,
    Volume2,
    VolumeX,
    BadgeCheck,
    Music2,
    Bookmark,
    Share2,
    MoreHorizontal,
    Flag,
    Trash2,
    X,
} from "lucide-react";
import { useAuth, useMessage, useTheme } from "../../global-components/src";
import { REFLEXO_API_URL, BUCKET_REFLEXO_URL, PACOCA_URL } from "../../global-components/src/utils/vars";
import { getImageUser } from "../../global-components/src/utils/auth";
import { errorMessage } from "../../global-components/src/utils/text";
import ImgVerified from "../../components/imgVerified/ImgVerified";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { getNativeKeyboardHeight, getNativeNavigationHeight, isIOSWebView, isWebView } from "../../utils/platform";
import { Reel } from "../../types";
import CommentsDrawer from "./comments-drawer";
import { setCachedReel, setCachedReels } from "./flow-cache";
import { createFlowReelsParams, FLOW_BUCKET } from "./flow-config";
import "./flow.css";

const mediaUrl = (path: string) => `${BUCKET_REFLEXO_URL}/${path}`;

const fmt = (n: number) =>
    n >= 1_000_000
        ? `${(n / 1_000_000).toFixed(1)}M`
        : n >= 1000
        ? `${(n / 1000).toFixed(1)}k`
        : String(n);

// ─── Hook: fetch feed com cursor ────────────────────────────────────────────
function useReelsFeed(savedOnly = false) {
    const [reels, setReels] = useState<Reel[]>([]);
    const [cursor, setCursor] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [end, setEnd] = useState(false);
    const { token } = useAuth();
    const headers = () => ({
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
    });

    const load = useCallback(async () => {
        if (loading || end) return;
        setLoading(true);
        try {
            const params = createFlowReelsParams(cursor);
            if (savedOnly) params.set("saved", "1");
            const { data } = await axios.get(`${REFLEXO_API_URL}/reels?${params}`, {
                headers: headers(),
            });
            if (!data.reels.length) {
                setEnd(true);
                return;
            }
            setCachedReels(data.reels);
            setReels((prev) => {
                const existingIds = new Set(prev.map((r) => r.id));
                const fresh = (data.reels as Reel[]).filter((r) => !existingIds.has(r.id));
                return [...prev, ...fresh];
            });
            setCursor(data.next_cursor);
        } catch {
            setEnd(true);
        } finally {
            setLoading(false);
        }
    }, [loading, end, cursor, savedOnly]);

    return { reels, setReels, load, loading, end };
}

// ─── Action Button ───────────────────────────────────────────────────────────
function ActionButton({
    icon,
    count,
    active,
    onClick,
    label,
}: {
    icon: React.ReactNode;
    count?: string;
    active?: boolean;
    onClick?: () => void;
    label: string;
}) {
    return (
        <button
            className="reel-action-btn"
            onClick={onClick}
            aria-label={label}
            data-active={active ? "true" : undefined}
        >
            <span className="reel-action-icon">{icon}</span>
            {count !== undefined && <span className="reel-action-count">{count}</span>}
        </button>
    );
}

// ─── Menu de opções (excluir / denunciar) ────────────────────────────────────
function ReelOptionsMenu({
    isOwner,
    onDelete,
    onReport,
    onClose,
}: {
    isOwner: boolean;
    onDelete: () => void;
    onReport: (reason: string) => void;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const [reportOpen, setReportOpen] = useState(false);
    const [reason, setReason] = useState("");

    if (reportOpen) {
        return (
            <div className="reel-report-overlay" onClick={onClose}>
                <div className="reel-report-box" onClick={(e) => e.stopPropagation()}>
                    <div className="reel-report-header">
                        <span>{t("flow.actions.report")}</span>
                        <button className="reel-drawer-close" onClick={onClose}>
                            <X size={16} />
                        </button>
                    </div>
                    <textarea
                        className="reel-report-textarea"
                        maxLength={500}
                        placeholder={t("flow.report.reasonPlaceholder")}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={4}
                    />
                    <button className="reel-report-submit" onClick={() => onReport(reason)}>
                        {t("flow.report.submit")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="reel-options-overlay" onClick={onClose}>
            <div className="reel-options-menu" onClick={(e) => e.stopPropagation()}>
                {isOwner ? (
                    <button className="reel-options-item reel-options-danger" onClick={onDelete}>
                        <Trash2 size={16} /> {t("flow.actions.delete")}
                    </button>
                ) : (
                    <button className="reel-options-item" onClick={() => setReportOpen(true)}>
                        <Flag size={16} /> {t("flow.actions.report")}
                    </button>
                )}
            </div>
        </div>
    );
}

// ─── Single Reel Card ────────────────────────────────────────────────────────
function ReelCard({
    reel,
    isActive,
    onLikeToggle,
    onSaveToggle,
    onDeleted,
}: {
    reel: Reel;
    isActive: boolean;
    onLikeToggle: (id: number, liked: boolean, delta: number) => void;
    onSaveToggle: (id: number, saved: boolean) => void;
    onDeleted: (id: number) => void;
}) {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [muted, setMuted] = useState(true);
    const [showComments, setShowComments] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [liked, setLiked] = useState(!!reel.liked);
    const [likes, setLikes] = useState(reel.total_likes);
    const [saved, setSaved] = useState(!!reel.saved);
    const [heartAnim, setHeartAnim] = useState(false);
    const [captionExpanded, setCaptionExpanded] = useState(false);
    const [bottomPadding, setBottomPadding] = useState(window.innerWidth <= 850 ? 60 : 10);
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const { setMessage } = useMessage();
    const isOwner = !!user && user.id === reel.id_user;
    const headers = () => ({
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
    });

    useEffect(() => {
        const handleKeyboardHeightChange = () => {
            const heightKeyboard = getNativeKeyboardHeight();
            const nativeNavigationHeight = getNativeNavigationHeight() ?? 0;
            setBottomPadding(heightKeyboard ? heightKeyboard + nativeNavigationHeight + 60 : nativeNavigationHeight + 60);
        };
        window.addEventListener("keyboardHeightChange", handleKeyboardHeightChange);
        return () => window.removeEventListener("keyboardHeightChange", handleKeyboardHeightChange);
    }, []);

    useEffect(() => {
        if (!isWebView()) return;
        const ajustaNewApp = () => {
            const nativeNavigationHeight = getNativeNavigationHeight();
            if (nativeNavigationHeight && nativeNavigationHeight > 0) {
                setBottomPadding(nativeNavigationHeight + 60);
            }
        };
        ajustaNewApp();
        window.addEventListener("navigationBarHeightChange", ajustaNewApp);
        return () => window.removeEventListener("navigationBarHeightChange", ajustaNewApp);
    }, []);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (isActive) {
            v.play().catch(() => {});
            axios.post(`${REFLEXO_API_URL}/reels/${reel.id}/view`, {}, { headers: headers() }).catch(() => {});
        } else {
            v.pause();
            v.currentTime = 0;
        }
    }, [isActive, reel.id]);

    const triggerHeart = () => {
        setHeartAnim(true);
        setTimeout(() => setHeartAnim(false), 700);
    };

    const toggleLike = async () => {
        const newLiked = !liked;
        const delta = newLiked ? 1 : -1;
        setLiked(newLiked);
        setLikes((l) => l + delta);
        if (newLiked) triggerHeart();
        onLikeToggle(reel.id, newLiked, delta);
        await axios.post(`${REFLEXO_API_URL}/reels/${reel.id}/like`, {}, { headers: headers() }).catch(() => {});
    };

    const toggleSave = async () => {
        const newSaved = !saved;
        setSaved(newSaved);
        onSaveToggle(reel.id, newSaved);
        try {
            await axios.post(`${REFLEXO_API_URL}/reels/${reel.id}/save`, {}, { headers: headers() });
            setMessage(newSaved ? t("flow.actions.savedSuccess") : t("flow.actions.unsavedSuccess"), "success");
        } catch (err: any) {
            setSaved(!newSaved);
            onSaveToggle(reel.id, !newSaved);
            setMessage(errorMessage(err) ?? t("generics.error"), "error");
        }
    };

    const share = async () => {
        const url = `${PACOCA_URL}/flow/${reel.id}`;
        if (navigator.share) {
            try {
                await navigator.share({ url, title: reel.caption ?? undefined });
                return;
            } catch {
                return;
            }
        }
        try {
            await navigator.clipboard.writeText(url);
            setMessage(t("flow.actions.linkCopied"), "success");
        } catch {
            setMessage(t("generics.error"), "error");
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t("flow.actions.confirmDelete"))) return;
        try {
            await axios.delete(`${REFLEXO_API_URL}/reels/${reel.id}`, { headers: headers() });
            setMessage(t("flow.actions.deletedSuccess"), "success");
            onDeleted(reel.id);
        } catch (err: any) {
            setMessage(errorMessage(err) ?? t("generics.error"), "error");
        }
        setShowOptions(false);
    };

    const handleReport = async (reason: string) => {
        try {
            await axios.post(`${REFLEXO_API_URL}/reels/${reel.id}/report`, { reason: reason || undefined }, { headers: headers() });
            setMessage(t("flow.actions.reportedSuccess"), "success");
        } catch (err: any) {
            setMessage(errorMessage(err) ?? t("generics.error"), "error");
        }
        setShowOptions(false);
    };

    const doubleTap = () => {
        if (!liked) toggleLike();
        else triggerHeart();
    };

    return (
        <div className="reel-card">
            <div className="reel-video-wrapper" onDoubleClick={doubleTap}>
                <video
                    ref={videoRef}
                    src={mediaUrl(reel.video_path)}
                    poster={reel.thumbnail_path ? mediaUrl(reel.thumbnail_path) : undefined}
                    className="video-reels"
                    loop
                    muted={muted}
                    playsInline
                />

                <div className="reel-video-gradient" />

                <div className="reel-video-footer" style={{ paddingBottom: bottomPadding }}>
                    <div className="reel-user-row">
                        <img
                            src={getImageUser(reel.user.img_account ?? undefined)}
                            alt={reel.user.user_name}
                            onError={(e) => {
                                e.currentTarget.src = "/img/img-account.png";
                            }}
                            className="reel-avatar"
                        />
                        <div className="reel-user-info">
                            <span className="reel-username" onClick={() => navigate(`/@${reel.user.user_name}`)}>
                                @{reel.user.user_name}
                                <ImgVerified classNameProp="verified-reel" user={reel.user} />
                            </span>
                            <span className="reel-name">{reel.user.name}</span>
                        </div>
                    </div>

                    {reel.caption && (
                        <p className={`reel-caption${captionExpanded ? " expanded" : ""}`} onClick={() => setCaptionExpanded((v) => !v)}>
                            {reel.caption}
                        </p>
                    )}

                    {reel.audio_title && (
                        <div className="reel-audio-row">
                            <Music2 size={12} className="reel-music-icon" />
                            <span className="reel-audio-title">{reel.audio_title}</span>
                        </div>
                    )}
                </div>

                {heartAnim && (
                    <div className="reel-heart-burst" aria-hidden>
                        <Heart size={80} fill="white" color="white" />
                    </div>
                )}
            </div>

            <div className="reel-actions">
                <ActionButton
                    label={t("flow.actions.like")}
                    icon={<Heart size={26} fill={liked ? "#ef4444" : "none"} color={liked ? "#ef4444" : "currentColor"} strokeWidth={liked ? 0 : 2} />}
                    count={fmt(likes)}
                    active={liked}
                    onClick={toggleLike}
                />

                <ActionButton
                    label={t("flow.actions.comments")}
                    icon={<MessageCircle size={26} strokeWidth={2} />}
                    count={fmt(reel.total_comments)}
                    onClick={() => setShowComments(true)}
                />

                <ActionButton label={t("flow.actions.share")} icon={<Share2 size={24} strokeWidth={2} />} onClick={share} />

                <ActionButton
                    label={t("flow.actions.save")}
                    icon={<Bookmark size={24} fill={saved ? "currentColor" : "none"} strokeWidth={2} />}
                    active={saved}
                    onClick={toggleSave}
                />

                <ActionButton label={t("flow.actions.views")} icon={<Eye size={24} strokeWidth={2} />} count={fmt(reel.total_views)} />

                <ActionButton
                    label={muted ? t("flow.actions.unmute") : t("flow.actions.mute")}
                    icon={muted ? <VolumeX size={22} strokeWidth={2} /> : <Volume2 size={22} strokeWidth={2} />}
                    onClick={() => setMuted((m) => !m)}
                />

                <ActionButton
                    label={t("flow.actions.options")}
                    icon={<MoreHorizontal size={24} strokeWidth={2} />}
                    onClick={() => setShowOptions(true)}
                />
            </div>

            {showComments && <CommentsDrawer reelId={reel.id} total={reel.total_comments} onClose={() => setShowComments(false)} />}

            {showOptions && (
                <ReelOptionsMenu
                    isOwner={isOwner}
                    onDelete={handleDelete}
                    onReport={handleReport}
                    onClose={() => setShowOptions(false)}
                />
            )}
        </div>
    );
}

// ─── Feed principal ──────────────────────────────────────────────────────────
export default function ReelsFeed({
    initialReelId,
    newReel,
    onNewReelConsumed,
    savedOnly = false,
}: {
    initialReelId?: number;
    newReel?: Reel | null;
    onNewReelConsumed?: () => void;
    savedOnly?: boolean;
}) {
    const { reels, setReels, load, loading } = useReelsFeed(savedOnly);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const hasLoaded = useRef(false);
    const { token } = useAuth();
    const { isDarkMode } = useTheme();
    const location = useLocation();
    const statusbarStyleClose = useMemo(
        () => (isDarkMode ? "#000" : "#fff"),
        [window.ReactNativeWebView, window.ReactNativeWebView?.postMessage, window.innerWidth, window.location.pathname, isDarkMode]
    );

    useEffect(() => {
        if (hasLoaded.current) return;
        hasLoaded.current = true;
        (async () => {
            if (initialReelId) {
                try {
                    const { data } = await axios.get(`${REFLEXO_API_URL}/reels/${initialReelId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    setCachedReel(data.reel);
                    setReels((prev) => [data.reel, ...prev]);
                } catch {
                    // segue para o feed normal mesmo se o reel do link não existir mais
                }
            }
            await load();
        })();
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const items = container.querySelectorAll("[data-reel-index]");
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        const idx = Number((e.target as HTMLElement).dataset.reelIndex);
                        setActiveIndex(idx);
                        if (idx >= reels.length - 2) load();
                    }
                });
            },
            { threshold: 0.7 }
        );
        items.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [reels.length, load]);

    const handleLikeToggle = (id: number, liked: boolean, delta: number) => {
        setReels((prev) => prev.map((r) => (r.id === id ? { ...r, liked: liked ? 1 : 0, total_likes: r.total_likes + delta } : r)));
    };

    const handleSaveToggle = (id: number, saved: boolean) => {
        setReels((prev) => prev.map((r) => (r.id === id ? { ...r, saved: saved ? 1 : 0 } : r)));
    };

    const handleDeleted = (id: number) => {
        setReels((prev) => prev.filter((r) => r.id !== id));
    };

    useEffect(() => {
        if (!newReel) return;
        setCachedReel(newReel);
        setReels((prev) => (prev.some((r) => r.id === newReel.id) ? prev : [newReel, ...prev]));
        onNewReelConsumed?.();
    }, [newReel]);

    useEffect(() => {
        if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") {
            if (location.pathname.startsWith("/flow")) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ statusBarStyle: "transparent" }));
                return;
            }
            window.ReactNativeWebView.postMessage(JSON.stringify({ statusBarStyle: statusbarStyleClose }));
        }
    }, [activeIndex, location.pathname]);

    return (
        <div ref={containerRef} className="reels-feed">
            {reels.map((reel, i) => (
                <div key={reel.id} data-reel-index={i} className="reel-slot">
                    <ReelCard
                        reel={reel}
                        isActive={i === activeIndex}
                        onLikeToggle={handleLikeToggle}
                        onSaveToggle={handleSaveToggle}
                        onDeleted={handleDeleted}
                    />
                </div>
            ))}

            {loading && (
                <div className="reel-loading-slot">
                    <Spinner size="32" borderWidth="2.5px" className="reel-spinner" />
                </div>
            )}

            {!loading && !reels.length && (
                <div className="reel-slot reel-empty-slot">
                    <span className="span-empty">
                        <Music2 color="#fff" className="icon-empty" />
                    </span>
                </div>
            )}
        </div>
    );
}

export { FLOW_BUCKET, mediaUrl, fmt };
